import {
  ActivityFilterKey,
  isGuestWorkspaceRole,
  useAssetIntegrationReadings,
  useCurrentAccount,
  useRoles,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  ActivityFilterScreen,
  ActivityFilterScreenHandle,
  ActivityList,
  type ColumnData,
  SlideAction,
  SlideLayout,
  type SlideLayoutLocationState,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { StyleSheet, View } from 'react-native';
import { ActivityFilters } from '../../activity/components/activityFilters';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import { AssetIntegrationReadingsSection } from './assetIntegrationReadingsSection';
import { AssetRouteProvider, useAssetRouting } from '../assetRouteContext';
import { AssetChatHeaderButton } from './assetChatHeaderButton';
import { ALL_ACTIVITIES_QUERY_KEY, isActivitiesParent } from '../assetUtils';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

export const AssetDetail = () => {
  const { assetId, issueId, digestKey, workspaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const filterRef = useRef<ActivityFilterScreenHandle>(null);
  const { workspaceAsset } = useWorkspaceAsset(assetId ?? '', !assetId);
  const { account, isLoading: isAccountLoading } = useCurrentAccount();
  const { roles, isLoading: isRolesLoading } = useRoles();
  const assetWorkspaceId = workspaceAsset?.workspace?.id;

  // ALLOWLIST, not deny-list (plan 0030 Phase 5): a guest gets exactly the
  // ordinary two-column page narrowed to the header and their own
  // conversation. The filter/add controls, the third-column filter panel,
  // and (in RightColumn below) integration readings all live behind this
  // one guard, so a section added later here is off for a guest by default.
  // Fails closed: unresolved account/roles/workspace data reads as guest.
  const isGuestOfAssetWorkspace =
    isAccountLoading || isRolesLoading || !assetWorkspaceId
      ? true
      : isGuestWorkspaceRole(assetWorkspaceId, account?.workspaceRoles, roles);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [count, setCount] = useState<number>();
  // The asset can be reached from four contexts: standalone (`/assets/...`),
  // an activity conversation (`/activities/:issueId/asset/...`), the
  // integration digest panel (`/activities/digest/:digestKey/asset/...`), or a
  // workspace (`/workspaces/:workspaceId/asset/...`). The digest case
  // (GRIPP #2887) keeps the digest prefix so back returns to the digest panel
  // rather than /assets. `digestKey` is already URL-encoded here (carries the
  // `%7C` pipe) — pass it through untouched so the prefix round-trips.
  const isActivitiesContext = Boolean(issueId) || Boolean(digestKey);
  const assetBasePath = workspaceId
    ? `/workspaces/${workspaceId}/asset/${assetId}`
    : digestKey
      ? `/activities/digest/${digestKey}/asset/${assetId}`
      : issueId
        ? `/activities/${issueId}/asset/${assetId}`
        : `/assets/${assetId}`;
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  if (!assetId) {
    return null;
  }

  const leftColumn: ColumnData = {
    title: workspaceId ? 'admin.menu.workspaces' : 'assets.menu.title',
    mainComponent: (
      <AssetRouteProvider
        value={{
          basePath: assetBasePath,
          listPath: workspaceId
            ? '/workspaces'
            : isActivitiesContext
            ? '/activities'
            : '/assets',
          isWorkspaceMode: !!workspaceId,
          issueId,
        }}
      >
        <AssetCard assetId={assetId} />
      </AssetRouteProvider>
    ),
    // Off the allowlist for a guest — edit/add-issue controls, not the
    // header or their own conversation.
    headerComponent: isGuestOfAssetWorkspace ? null : (
      <View style={styles.headerActionsRow}>
        {/* Chat icon sits in the column header row beside the existing
            edit/add actions. Off the allowlist for a guest via the
            isGuestOfAssetWorkspace ternary above, and again via
            AssetChatHeaderButton's own gate as defense in depth. */}
        <AssetChatHeaderButton
          assetId={assetId}
          routeWorkspaceId={workspaceId}
        />
        <AssetFilters assetId={assetId} />
      </View>
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: () =>
      navigate(workspaceId || isActivitiesContext ? '../../../..' : '../..', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      }),
    disableScroll: false,
  };

  const rightColumn: ColumnData = {
    title: workspaceId ? 'admin.menu.workspaces' : 'assets.menu.title',
    rawTitle: workspaceAsset?.asset?.name,
    count,
    mainComponent: (
      <RightColumn
        assetId={assetId}
        onCountChange={setCount}
        isGuestOfAssetWorkspace={isGuestOfAssetWorkspace}
      />
    ),
    // Off the allowlist for a guest — the filter/search control over their
    // one conversation has nothing to filter.
    headerComponent: isGuestOfAssetWorkspace ? null : (
      <ActivityFilters
        assetId={assetId}
        showAddButton={false}
        onFilterChange={() => setIsFilterPanelOpen(true)}
      />
    ),
    showBackButton: true,
    disableScroll: true,
    onBackButtonPress: () => {
      navigate(workspaceId ? '../../../..' : '../..', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      });
    },
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
  };

  // Unreachable for a guest anyway (its only trigger is the hidden
  // ActivityFilters header above), gated directly too as defense in depth.
  const thirdColumn = isFilterPanelOpen && !isGuestOfAssetWorkspace
    ? {
        title: 'assets.menu.title',
        mainComponent: (
          <ActivityFilterScreen
            ref={filterRef}
            mode="page"
            isVisible={true}
            filterKey={ActivityFilterKey.AssetActivity}
            assetId={assetId}
            bottomBar={() => null}
          />
        ),
        headerComponent: (
          <HeaderResetButton onReset={() => filterRef.current?.onReset()} />
        ),
        showBackButton: true,
        disableScroll: true,
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};

const RightColumn = ({
  assetId,
  onCountChange,
  isGuestOfAssetWorkspace,
}: {
  assetId: string;
  onCountChange?: (count: number) => void;
  isGuestOfAssetWorkspace: boolean;
}) => {
  const { prepareSlideTransition } = useSlideLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { basePath } = useAssetRouting(assetId);
  // Read the context params from the route (present on
  // activities/:issueId/asset/:assetId/activity/all and the digest equivalent
  // activities/digest/:digestKey/asset/:assetId/activity/all). digestKey is
  // URL-encoded here (carries the %7C pipe) — pass it through untouched.
  const { issueId: contextIssueId, digestKey: contextDigestKey } = useParams();
  // Shares the readings section's query cache — used only to suppress the
  // contradictory "No activity" empty state when readings render above.
  // Off the allowlist for a guest, and Guest holds no assetroutine.*
  // permission at all (data-seed/roles.json), so the query itself is
  // disabled rather than merely hiding its result.
  const { readings } = useAssetIntegrationReadings(
    assetId,
    isGuestOfAssetWorkspace
  );

  const onIssueSelected = async (issueId: string) => {
    // Use the outer context param (digest key or issue ID) for the first
    // segment, not the newly-selected issueId — otherwise the original context
    // is lost and back would bounce out of the digest/conversation chain.
    const url =
      isActivitiesParent(location) && contextDigestKey
        ? `/activities/digest/${contextDigestKey}/asset/${assetId}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`
        : isActivitiesParent(location) && contextIssueId
          ? `/activities/${contextIssueId}/asset/${assetId}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`
          : `${basePath}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`;
    prepareSlideTransition(SlideAction.NEXT);
    navigate(url, {
      state: { slideLayoutInitialTransition: SlideAction.NEXT },
    });
  };

  return (
    <View style={{ flex: 1 }}>
      {!isGuestOfAssetWorkspace && (
        <>
          <AssetIntegrationReadingsSection assetId={assetId} />
          {/* Testability seam (plan 0030 Phase 5.2b): a permanent,
              content-free marker proving the allowlist gate itself — not any
              one fold's wiring — hides whatever lives inside it by default.
              A section added here later belongs inside this same guard; see
              assetDetail.spec.tsx's "ALLOWLIST PROOF" specs. */}
          <View testID="asset-detail-guest-allowlist-canary" />
        </>
      )}
      <ActivityList
        filterKey={ActivityFilterKey.AssetActivity}
        onIssueSelected={onIssueSelected}
        assetId={assetId}
        showAssetName={false}
        showImage={false}
        pageSize={50}
        showTopBar={false}
        hideTextEmpty={readings.length > 0}
        onActivityCountChange={onCountChange}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  headerActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
});
